import {
  useQuery,
  useMutation,
  keepPreviousData,
  useQueryClient
} from "@tanstack/react-query";
import qs from "qs";
import api from "@services/api";

async function fetchCategories(filters, page, pageSize) {
  const query = qs.stringify(
    { ...filters, page, page_size: pageSize },
    { arrayFormat: "indices", encodeValuesOnly: true }
  );
  const { data } = await api.get(`/catalog/categories?${query}`);
  return data;
}

export function useCategoriesQuery(filters, page, pageSize) {
  return useQuery({
    queryKey: ["catalog", "categories", filters, page, pageSize],
    queryFn: () => fetchCategories(filters, page, pageSize),
    placeholderData: keepPreviousData
  });
}

export function useCategoryQuery(id) {
  return useQuery({
    queryKey: ["catalog", "category", id],
    queryFn: async () => {
      const { data } = await api.get(`/catalog/categories/${id}`);
      return data;
    },
    enabled: !!id
  });
}

export function useSaveCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.put(`/catalog/categories/${id}`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      const { id } = variables || {};
      qc.invalidateQueries({ queryKey: ["catalog", "categories"] });
      if (id) {
        qc.invalidateQueries({ queryKey: ["catalog", "category", id] });
      }
    }
  });
}

export function useCreateCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/catalog/categories", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "categories"] });
    }
  });
}

// Products inside a category (e.g. the Shop) — list, add, remove.
export function useCategoryProductsQuery(id) {
  return useQuery({
    queryKey: ["catalog", "category-products", id],
    queryFn: async () => {
      const { data } = await api.get(`/catalog/categories/${id}/products`);
      return data?.data || [];
    },
    enabled: !!id
  });
}

export function useAttachCategoryProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, productId }) => {
      const { data } = await api.post(`/catalog/categories/${id}/products`, { product_id: productId });
      return data;
    },
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ["catalog", "category-products", id] });
    }
  });
}

export function useDetachCategoryProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, productId }) => {
      const { data } = await api.delete(`/catalog/categories/${id}/products/${productId}`);
      return data;
    },
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ["catalog", "category-products", id] });
    }
  });
}

export function useProductsQuery(filters, page, pageSize) {
  return useQuery({
    queryKey: ["catalog", "products", filters, page, pageSize],
    queryFn: async () => {
      const { data } = await api.get("/catalog/products", {
        params: { ...filters, page, page_size: pageSize }
      });
      return data;
    },
    placeholderData: keepPreviousData
  });
}

export function useProductQuery(id) {
  return useQuery({
    queryKey: ["catalog", "product", id],
    queryFn: async () => {
      const { data } = await api.get(`/catalog/products/${id}`);
      return data;
    },
    enabled: !!id
  });
}

export function useSaveProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.put(`/catalog/products/${id}`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      const { id } = variables || {};
      qc.invalidateQueries({ queryKey: ["catalog", "products"] });
      if (id) {
        qc.invalidateQueries({ queryKey: ["catalog", "product", id] });
      }
    }
  });
}

export function useCreateProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/catalog/products", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "products"] });
    }
  });
}

export function useDeleteProductsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids) => {
      const { data } = await api.delete("/catalog/products", { data: { ids } });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "products"] });
    }
  });
}

export function useDuplicateProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(`/catalog/products/${id}/duplicate`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "products"] });
    }
  });
}

/* ── Product Specials ── */

export function useProductSpecialsQuery(productId) {
  return useQuery({
    queryKey: ["catalog", "product-specials", productId],
    queryFn: async () => {
      const { data } = await api.get("/oc-product-special", {
        params: { product_id: productId }
      });
      return data;
    },
    enabled: !!productId
  });
}

export function useCreateProductSpecialMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/oc-product-special", payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["catalog", "product-specials", variables.product_id] });
    }
  });
}

export function useUpdateProductSpecialMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.put(`/oc-product-special/${id}`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["catalog", "product-specials", variables.payload.product_id] });
    }
  });
}

export function useDeleteProductSpecialMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      const { data } = await api.delete(`/oc-product-special/${id}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "product-specials"] });
    }
  });
}

